import { uid } from '@/firebase'
import { TeamMember } from '@/helpers/fs/teams/teamMember'
import { BookingPage } from '@/helpers/fs/users/bookingPage'
import { getQuery } from '@/helpers/hasQuery'
import { sortBy } from '@WombatTechnology/nitte-time-lib'
import { flatten, isEmpty, isNull } from 'es-toolkit/compat'
import {
  Timestamp,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useTeamStore } from './teamStore'

type State = {
  searchInput: ''
  attendeeFilterMembers: TeamMember[]
  isLoading: boolean
  bookingPages: BookingPage[]
  previewingPage: BookingPage | null
  justActionPageId: string | null
}

export const usePagesStore = defineStore('pages', {
  state: (): State => {
    return {
      searchInput: '',
      attendeeFilterMembers: [],
      isLoading: true,
      bookingPages: [],
      previewingPage: null,
      justActionPageId: null
    }
  },
  actions: {
    async setup() {
      const [userPages, sharedPages] = await Promise.all([
        this.initUserPages(),
        this.initSharedPages()
      ])
      const allPages = userPages.concat(flatten(sharedPages))
      this.$patch({
        bookingPages: allPages,
        isLoading: false,
        justActionPageId: getQuery('pageId')
      })
    },
    // 自分のページ一覧を取得
    async initUserPages() {
      // NOTE: ページのシェアが始まればクエリを修正する必要あり
      const q = query(
        BookingPage.collection(uid()!),
        orderBy('createdAt', 'desc'),
        limit(200)
      )
      const qs = await getDocs(q)
      const pages: State['bookingPages'] = []
      // NOTE:
      // * onSnapShotすると無駄に呼ばれる
      // * 呼びたいタイミングが明確にある
      // ので、onSnapShotは使わずにrefetchしたいときはsetupを再度コールする
      await Promise.all(
        qs.docs.map(async (d) => {
          // where句とorder句は1つのクエリで同じフィールドにしかかけられない制約があるため
          // こっち側で除外している。
          if (d.ref.id !== 'preview') {
            const page = await BookingPage.getPage(uid()!, d.ref.id)
            if (isNull(page)) {
              return
            }
            pages.push(page)
          }
        })
      )
      return pages
    },
    async initSharedPages() {
      const currentTeam = useTeamStore().currentTeam

      if (isEmpty(currentTeam)) return
      // 現在のチームで自分に共有されたページを取得する
      return BookingPage.getSharedPages(uid()!, currentTeam.id)
    },
    async forceRefreshPreview() {
      await updateDoc(BookingPage.doc(uid()!, 'preview'), {
        updatedAt: Timestamp.fromDate(new Date())
      })
    }
  },
  getters: {
    hasNoPages(state): boolean {
      return isEmpty(state.bookingPages)
    },
    pages(state): BookingPage[] {
      // 調整回数で降順ソートする
      return sortBy(state.bookingPages, (p) => p.metaData.bookingCount, 'desc')
    }
  }
})
